import React, { useEffect, useState } from "react";

import {
  calculatePrice,
  getCurrencySymbol,
} from "../../utilities/utilities.ts";
import { addItem } from "../cart/cartSlice.ts";
import { loadData } from "./inventorySlice.ts";
import { filterInventory } from "../../utilities/utilities.ts";
import { InventoryType } from "../../types/inventory";

const productDescriptions: Record<string, string> = {
  "Hello World Hat":
    "A relaxed-fit hat with stitched lettering for daily terminal sessions.",
  "Learn From Home Joggers":
    "Soft fleece joggers with a tapered cut for desk-to-gym comfort.",
  "Nikky Tee Shirt":
    "Premium cotton tee with a laid-back fit and durable neck rib.",
  "Java Tee":
    "A lightweight tee with a clean screenprint and breathable weave.",
  "Fedora Hat":
    "Structured brim and tonal band for standout conference-day style.",
  "Python Tee":
    "A classic crew-neck shirt made for code reviews and coffee runs.",
  "SQL Tee":
    "Minimalist print on ultra-soft fabric for everyday wear.",
  "Hello World Hoodie":
    "Heavyweight hoodie with brushed interior and warm kangaroo pocket.",
};

const getProductCategory = (name: string) => {
  if (name.includes("Hat")) {
    return "Accessories";
  }
  if (name.includes("Hoodie") || name.includes("Joggers")) {
    return "Outerwear";
  }
  return "T-Shirts";
};

const getProductDescription = (name: string) => {
  return productDescriptions[name] || "Thoughtfully designed developer apparel.";
};

export const Inventory = ({
  inventory,
  searchTerm,
  currencyFilter,
  dispatch,
}) => {
  const [lastAddedItemName, setLastAddedItemName] = useState("");
  const [detailItem, setDetailItem] = useState<InventoryType | null>(null);

  const onMount = () => {
    dispatch(loadData());
  };
  useEffect(onMount, [dispatch]);

  useEffect(() => {
    if (!lastAddedItemName) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setLastAddedItemName("");
    }, 900);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [lastAddedItemName]);

  useEffect(() => {
    if (!detailItem) {
      return;
    }

    const handleEscapePress = (event) => {
      if (event.key === "Escape") {
        setDetailItem(null);
      }
    };

    window.addEventListener("keydown", handleEscapePress);
    return () => {
      window.removeEventListener("keydown", handleEscapePress);
    };
  }, [detailItem]);

  const onClickHandler = (inventoryItem: InventoryType) => {
    dispatch(addItem(inventoryItem));
    setLastAddedItemName(inventoryItem.name);
  };

  if (inventory.length === 0) {
    return (
      <p className="inventory-fallback">
        Sorry, no products are currently available.
      </p>
    );
  }

  const filteredInventory = filterInventory(inventory, searchTerm);

  return (
    <section id="products" className="inventory-section">
      <div className="section-heading">
        <p className="section-kicker">Featured Collection</p>
        <h2 className="section-title">Built for shipping fast.</h2>
      </div>

      {filteredInventory.length === 0 ? (
        <p className="inventory-fallback">
          No products match "{searchTerm}". Try a different search term.
        </p>
      ) : (
        <ul id="inventory-container">
          {filteredInventory.map(createInventoryItem)}
        </ul>
      )}

      {detailItem && createProductDetailView(detailItem)}
    </section>
  );

  function createInventoryItem(inventoryItem: InventoryType, index: number) {
    const { price, name, img } = inventoryItem;
    const displayPrice = calculatePrice(price, currencyFilter);
    const isRecentlyAdded = lastAddedItemName === name;

    return (
      <li
        key={name}
        className={`item ${isRecentlyAdded ? "added" : ""}`}
        style={{ animationDelay: `${index * 70}ms` }}
      >
        <div className="item-image-wrap">
          <img src={img} alt={`${name} product`} />
        </div>
        <p className="item-category">{getProductCategory(name)}</p>
        <h3>{name}</h3>
        <p className="item-description">{getProductDescription(name)}</p>
        <p className="price">
          {getCurrencySymbol(currencyFilter)}
          {displayPrice.toFixed(2)} {currencyFilter}
        </p>
        <div className="item-actions">
          <button
            onClick={() => onClickHandler(inventoryItem)}
            className={`add-to-cart-button ${isRecentlyAdded ? "added" : ""}`}
          >
            {isRecentlyAdded ? "Added!" : "Add to Cart"}
          </button>
          <button
            type="button"
            onClick={() => setDetailItem(inventoryItem)}
            className="view-detail-button"
          >
            View Details
          </button>
        </div>
      </li>
    );
  }

  function createProductDetailView(inventoryItem: InventoryType) {
    const { price, name, img } = inventoryItem;
    const displayPrice = calculatePrice(price, currencyFilter);

    return (
      <div
        className="product-detail-backdrop"
        role="presentation"
        onClick={() => setDetailItem(null)}
      >
        <article
          className="product-detail-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-detail-title"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setDetailItem(null)}
            className="detail-close-button"
            aria-label="Close product details"
          >
            Close
          </button>

          <img src={img} alt={`${name} close-up`} />

          <div className="detail-content">
            <p className="item-category">{getProductCategory(name)}</p>
            <h3 id="product-detail-title">{name}</h3>
            <p className="item-description">{getProductDescription(name)}</p>
            <p className="price">
              {getCurrencySymbol(currencyFilter)}
              {displayPrice.toFixed(2)} {currencyFilter}
            </p>
            <button
              type="button"
              onClick={() => {
                onClickHandler(inventoryItem);
                setDetailItem(null);
              }}
              className="add-to-cart-button detail-add-button"
            >
              Add to Cart
            </button>
          </div>
        </article>
      </div>
    );
  }
};
